/**
 * OnboardingChatbot - Interactive AI assistant for platform guidance
 *
 * Floating chatbot widget that helps users navigate the platform,
 * understand features, and get step-by-step guidance.
 */

import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Minus, Send, Bot, ChevronRight } from 'lucide-react';
import { useChatbotStore, QuickAction } from '@/stores/chatbotStore';
import {
  searchKnowledgeBase,
  getTopicById,
  getRelatedTopics,
  quickActions,
  HelpTopic,
} from './knowledgeBase';

// Typing indicator component
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 py-2 px-1">
      <motion.span
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1, repeat: Infinity }}
        className="w-2 h-2 rounded-full bg-primary/60"
      />
      <motion.span
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
        className="w-2 h-2 rounded-full bg-primary/60"
      />
      <motion.span
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
        className="w-2 h-2 rounded-full bg-primary/60"
      />
    </div>
  );
}

// Quick action button component
function QuickActionButton({
  action,
  onClick,
}: {
  action: QuickAction;
  onClick: (action: string) => void;
}) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(action.action)}
      className="flex items-center gap-2 px-3 py-2 bg-primary/5 hover:bg-primary/10 border border-primary/20 rounded-lg text-sm text-foreground transition-colors text-left"
    >
      <span>{action.label}</span>
      <ChevronRight className="w-3 h-3 ml-auto text-muted-foreground" />
    </motion.button>
  );
}

// Message bubble component
function MessageBubble({
  message,
  onQuickAction,
}: {
  message: {
    id: string;
    type: 'user' | 'bot';
    content: string;
    quickActions?: QuickAction[];
    relatedTopics?: string[];
  };
  onQuickAction: (action: string) => void;
}) {
  const isUser = message.type === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`
          max-w-[85%] rounded-2xl px-4 py-3
          ${
            isUser
              ? 'bg-primary text-primary-foreground rounded-br-md'
              : 'bg-muted/50 border border-border rounded-bl-md'
          }
        `}
      >
        {/* Message content */}
        <div
          className={`text-sm whitespace-pre-wrap ${isUser ? '' : 'text-foreground'}`}
          dangerouslySetInnerHTML={{
            __html: message.content
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .replace(/\n/g, '<br />'),
          }}
        />

        {/* Quick actions */}
        {message.quickActions && message.quickActions.length > 0 && (
          <div className="mt-3 space-y-2">
            {message.quickActions.map((action) => (
              <QuickActionButton key={action.id} action={action} onClick={onQuickAction} />
            ))}
          </div>
        )}

        {/* Related topics */}
        {message.relatedTopics && message.relatedTopics.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/50">
            <p className="text-xs text-muted-foreground mb-2">Powiązane tematy:</p>
            <div className="flex flex-wrap gap-1">
              {message.relatedTopics.map((topicId) => {
                const topic = getTopicById(topicId);
                if (!topic) return null;
                return (
                  <button
                    key={topicId}
                    onClick={() => onQuickAction(`topic:${topicId}`)}
                    className="text-xs px-2 py-1 bg-background hover:bg-muted rounded-full border border-border text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {topic.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Format topic content for chat display
function formatTopicResponse(topic: HelpTopic): string {
  return `**${topic.title}**\n\n${topic.content}`;
}

// Process user input and generate response
async function processUserInput(
  input: string,
  onResponse: (
    content: string,
    options?: { quickActions?: QuickAction[]; relatedTopics?: string[] }
  ) => void
) {
  const lowerInput = input.toLowerCase().trim();

  // Check for quick action patterns
  if (lowerInput.startsWith('topic:')) {
    const topicId = lowerInput.replace('topic:', '');
    const topic = getTopicById(topicId);

    if (topic) {
      const relatedTopics = getRelatedTopics(topic).map((t) => t.id);
      const response = formatTopicResponse(topic);

      // Add quick actions for related topics
      const responseActions: QuickAction[] = relatedTopics.slice(0, 2).map((relId) => {
        const relTopic = getTopicById(relId);
        return {
          id: `related-${relId}`,
          label: relTopic?.title || relId,
          action: `topic:${relId}`,
        };
      });

      onResponse(response, { quickActions: responseActions, relatedTopics: relatedTopics.slice(0, 3) });
      return;
    }
  }

  // Handle navigation commands
  if (lowerInput.startsWith('navigate:')) {
    const path = lowerInput.replace('navigate:', '');
    window.location.href = path;
    return;
  }

  // Search knowledge base for relevant topics
  const searchResults = searchKnowledgeBase(input);

  if (searchResults.length > 0) {
    const topResult = searchResults[0];
    const response = formatTopicResponse(topResult);
    const relatedTopics = searchResults.slice(1, 4).map((t) => t.id);

    const responseActions: QuickAction[] = [];

    // Add more results as quick actions
    if (searchResults.length > 1) {
      searchResults.slice(1, 3).forEach((result) => {
        responseActions.push({
          id: `result-${result.id}`,
          label: result.title,
          action: `topic:${result.id}`,
        });
      });
    }

    onResponse(response, { quickActions: responseActions, relatedTopics });
  } else {
    // No results found - offer suggestions
    const suggestions = quickActions.slice(0, 4);
    onResponse(
      'Nie znalazłem dokładnej odpowiedzi na to pytanie. Może któryś z tych tematów Ci pomoże?',
      {
        quickActions: suggestions.map((s) => ({
          id: s.id,
          label: `${s.icon} ${s.label}`,
          action: `topic:${s.topicId}`,
        })),
      }
    );
  }
}

// Main component
export function OnboardingChatbot() {
  const {
    isOpen,
    isMinimized,
    hasUnread,
    messages,
    isTyping,
    openChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    addUserMessage,
    addBotMessage,
    setTyping,
    markAsRead,
    markTopicVisited,
  } = useChatbotStore();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Handle sending message
  const handleSend = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;

    // Add user message
    addUserMessage(trimmedInput);
    setInputValue('');

    // Show typing indicator
    setTyping(true);

    // Simulate processing delay
    await new Promise((resolve) => setTimeout(resolve, 500 + Math.random() * 1000));

    // Process and respond
    await processUserInput(trimmedInput, (content, options) => {
      addBotMessage(content, options);

      // Mark topic as visited if applicable
      if (trimmedInput.startsWith('topic:')) {
        markTopicVisited(trimmedInput.replace('topic:', ''));
      }
    });
  };

  // Handle quick action click
  const handleQuickAction = async (action: string) => {
    if (action.startsWith('navigate:')) {
      const path = action.replace('navigate:', '');
      window.location.href = path;
      return;
    }

    if (action.startsWith('topic:')) {
      // Show as user selection
      const topicId = action.replace('topic:', '');
      const topic = getTopicById(topicId);
      if (topic) {
        addUserMessage(topic.title);
      }
    }

    setTyping(true);
    await new Promise((resolve) => setTimeout(resolve, 500));

    await processUserInput(action, (content, options) => {
      addBotMessage(content, options);

      if (action.startsWith('topic:')) {
        markTopicVisited(action.replace('topic:', ''));
      }
    });
  };

  // Handle keyboard events
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              openChat();
              markAsRead();
            }}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
          >
            <MessageCircle className="w-6 h-6" />
            {hasUnread && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-destructive rounded-full flex items-center justify-center"
              >
                <span className="text-[10px] text-white font-bold">!</span>
              </motion.span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              height: isMinimized ? 'auto' : 500,
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] bg-background rounded-2xl shadow-2xl border border-border overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="bg-primary px-4 py-3 flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-primary-foreground text-sm">SEO Assistant</h4>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-primary-foreground/80">Gotowy do pomocy</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={isMinimized ? maximizeChat : minimizeChat}
                  className="p-1.5 rounded-lg hover:bg-primary-foreground/10 transition-colors"
                >
                  <Minus className="w-4 h-4 text-primary-foreground" />
                </button>
                <button
                  onClick={closeChat}
                  className="p-1.5 rounded-lg hover:bg-primary-foreground/10 transition-colors"
                >
                  <X className="w-4 h-4 text-primary-foreground" />
                </button>
              </div>
            </div>

            {/* Messages area (hidden when minimized) */}
            {!isMinimized && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <AnimatePresence mode="popLayout">
                    {messages.map((message) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        onQuickAction={handleQuickAction}
                      />
                    ))}
                  </AnimatePresence>

                  {/* Typing indicator */}
                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex justify-start"
                    >
                      <div className="bg-muted/50 border border-border rounded-2xl rounded-bl-md px-4 py-2">
                        <TypingIndicator />
                      </div>
                    </motion.div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input area */}
                <div className="p-3 border-t border-border shrink-0">
                  <div className="flex items-center gap-2 bg-muted rounded-xl px-4 py-2.5">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Zadaj pytanie lub wybierz temat..."
                      className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!inputValue.trim()}
                      className="p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Help hint */}
                  <p className="text-[10px] text-muted-foreground text-center mt-2">
                    Zapytaj o dowolną funkcję platformy
                  </p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default OnboardingChatbot;
